import { useEffect } from 'react'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { qualifyClientRequest, type ClientRequestListItem, type RequestRejectionReason } from '../../api/cereri'
import { errorMessage } from '../../api/client'

const schema = z.object({
  qualification: z.enum(['InAnaliza', 'Acceptata', 'Respinsa']),
  rejectionReason: z.string().optional(),
  notes: z.string().optional(),
}).refine((v) => v.qualification !== 'Respinsa' || !!v.rejectionReason, {
  message: 'Motivul respingerii este obligatoriu.', path: ['rejectionReason'],
})
type FormValues = z.infer<typeof schema>

export default function QualifyRequestDialog({ target, onClose }: { target: ClientRequestListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema), defaultValues: { qualification: 'InAnaliza', rejectionReason: '', notes: '' },
  })
  const qualification = useWatch({ control, name: 'qualification' })

  useEffect(() => {
    if (target) reset({ qualification: target.qualification, rejectionReason: target.rejectionReason ?? '', notes: '' })
  }, [target, reset])

  const mutation = useMutation({
    mutationFn: (values: FormValues) => qualifyClientRequest(target!.id, {
      qualification: values.qualification,
      rejectionReason: values.qualification === 'Respinsa' ? (values.rejectionReason as RequestRejectionReason) : null,
      notes: values.notes || null,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comercial', 'cereri'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>Calificare cerere {target?.number}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="qualification" control={control} render={({ field }) => (
              <TextField {...field} select label="Calificare" fullWidth error={!!errors.qualification} helperText={errors.qualification?.message}>
                <MenuItem value="InAnaliza">În analiză</MenuItem>
                <MenuItem value="Acceptata">Acceptată</MenuItem>
                <MenuItem value="Respinsa">Respinsă</MenuItem>
              </TextField>
            )} />
            {qualification === 'Respinsa' && (
              <Controller name="rejectionReason" control={control} render={({ field }) => (
                <TextField {...field} select label="Motiv respingere" fullWidth
                  error={!!errors.rejectionReason} helperText={errors.rejectionReason?.message}>
                  <MenuItem value="CapacitateIndisponibila">Capacitate indisponibilă</MenuItem>
                  <MenuItem value="ProdusNeofertabil">Produs neofertabil</MenuItem>
                  <MenuItem value="ClientBlocatCredit">Client blocat pe credit</MenuItem>
                  <MenuItem value="CantitateSubMinim">Cantitate sub minim</MenuItem>
                  <MenuItem value="TermenImposibil">Termen imposibil</MenuItem>
                </TextField>
              )} />
            )}
            <Controller name="notes" control={control} render={({ field }) => (
              <TextField {...field} label="Note" fullWidth multiline minRows={2} />
            )} />
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
